import React, { useState } from 'react';
import { StudentLayout } from '../layouts/StudentLayout';
import { CheckIcon, CreditCardIcon, CalendarIcon, AlertCircleIcon, ArrowRightIcon } from 'lucide-react';
import { showToast } from '../ToastContainer';
interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  interval: 'month' | 'year';
  features: string[];
  isPopular?: boolean;
  isCurrent?: boolean;
}
interface PaymentMethod {
  id: string;
  type: 'card' | 'paypal';
  name: string;
  last4: string;
  expiryMonth?: string;
  expiryYear?: string;
  isDefault: boolean;
  brand?: string;
}
export const SubscriptionManagementPage = () => {
  const [currentPlan, setCurrentPlan] = useState<SubscriptionPlan>({
    id: 'basic',
    name: 'Basic Plan',
    price: 9.99,
    interval: 'month',
    features: ['Schedule up to 4 lessons per month', 'Access to basic learning materials', 'Chat with teachers', 'Attend virtual lessons'],
    isCurrent: true
  });
  const [availablePlans, setAvailablePlans] = useState<SubscriptionPlan[]>([{
    id: 'basic',
    name: 'Basic Plan',
    price: 9.99,
    interval: 'month',
    features: ['Schedule up to 4 lessons per month', 'Access to basic learning materials', 'Chat with teachers', 'Attend virtual lessons'],
    isCurrent: true
  }, {
    id: 'premium',
    name: 'Premium Plan',
    price: 19.99,
    interval: 'month',
    features: ['Schedule unlimited lessons', 'Access to all learning materials', 'Priority teacher support', 'Attend virtual lessons', 'Download sheet music', 'Progress tracking', 'Lesson recordings'],
    isPopular: true
  }, {
    id: 'family',
    name: 'Family Plan',
    price: 29.99,
    interval: 'month',
    features: ['Up to 4 family members', 'Schedule unlimited lessons', 'Access to all learning materials', 'Priority teacher support', 'Attend virtual lessons', 'Download sheet music', 'Progress tracking', 'Lesson recordings', 'Family dashboard']
  }]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([{
    id: '1',
    type: 'card',
    name: 'Sarah Johnson',
    last4: '4242',
    expiryMonth: '12',
    expiryYear: '2025',
    isDefault: true,
    brand: 'visa'
  }, {
    id: '2',
    type: 'paypal',
    name: 'sarah.johnson@example.com',
    last4: '',
    isDefault: false
  }]);
  const [billingInfo, setBillingInfo] = useState({
    nextBillingDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000),
    autoRenew: true
  });
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [isChangingPlan, setIsChangingPlan] = useState(false);
  const [isUpdatingBilling, setIsUpdatingBilling] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  // Format date
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  // Handle plan selection
  const handlePlanSelect = (planId: string) => {
    if (planId === currentPlan.id) {
      setSelectedPlan(null);
    } else {
      setSelectedPlan(planId);
    }
  };
  // Handle plan change
  const handleChangePlan = () => {
    if (!selectedPlan) return;
    setIsChangingPlan(true);
    // Simulate API call
    setTimeout(() => {
      const newPlan = availablePlans.find(plan => plan.id === selectedPlan);
      if (newPlan) {
        // Update current plan
        setCurrentPlan({
          ...newPlan,
          isCurrent: true
        });
        // Update available plans
        setAvailablePlans(availablePlans.map(plan => ({
          ...plan,
          isCurrent: plan.id === selectedPlan
        })));
        showToast.success(`Successfully switched to ${newPlan.name}`);
      }
      setSelectedPlan(null);
      setIsChangingPlan(false);
    }, 1500);
  };
  // Toggle auto-renew
  const toggleAutoRenew = () => {
    setIsUpdatingBilling(true);
    // Simulate API call
    setTimeout(() => {
      setBillingInfo({
        ...billingInfo,
        autoRenew: !billingInfo.autoRenew
      });
      showToast.success(billingInfo.autoRenew ? 'Auto-renewal has been turned off' : 'Auto-renewal has been turned on');
      setIsUpdatingBilling(false);
    }, 1000);
  };
  // Handle subscription cancellation
  const handleCancelSubscription = () => {
    if (!cancelReason.trim()) {
      showToast.warning('Please provide a reason for cancellation');
      return;
    }
    setIsUpdatingBilling(true);
    // Simulate API call
    setTimeout(() => {
      showToast.success('Your subscription has been cancelled');
      setShowCancelDialog(false);
      setIsUpdatingBilling(false);
      // In a real app, you would update the subscription status
      // and perhaps redirect to a cancellation confirmation page
    }, 1500);
  };
  return <StudentLayout data-id="element-7895">
      <div className="max-w-6xl mx-auto" data-id="element-7896">
        <div className="mb-6" data-id="element-7897">
          <h1 className="text-2xl font-serif font-bold text-white mb-2" data-id="element-7898">
            Subscription Management
          </h1>
          <p className="text-gray-300" data-id="element-7899">
            Manage your subscription plan and billing information
          </p>
        </div>
        {/* Current Plan */}
        <div className="bg-navy-800 rounded-xl p-6 mb-8" data-id="element-7900">
          <h2 className="text-lg font-medium text-white mb-4" data-id="element-7901">
            Current Subscription
          </h2>
          <div className="bg-navy-700 rounded-lg p-4 mb-6" data-id="element-7902">
            <div className="md:flex md:justify-between md:items-center" data-id="element-7903">
              <div data-id="element-7904">
                <div className="flex items-center" data-id="element-7905">
                  <h3 className="text-xl font-medium text-white" data-id="element-7906">
                    {currentPlan.name}
                  </h3>
                  <span className="ml-2 px-2 py-0.5 bg-deep-pink/20 text-deep-pink text-xs rounded-full" data-id="element-7907">
                    Current Plan
                  </span>
                </div>
                <p className="text-gray-300 mt-1" data-id="element-7908">
                  ${currentPlan.price.toFixed(2)}/{currentPlan.interval}
                </p>
              </div>
              <div className="mt-4 md:mt-0" data-id="element-7909">
                <button onClick={() => setShowCancelDialog(true)} className="px-4 py-2 border border-red-500 text-red-500 hover:bg-red-500 hover:text-white rounded-lg transition-colors text-sm" data-id="element-7910">
                  Cancel Subscription
                </button>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-7911">
            <div data-id="element-7912">
              <h3 className="text-sm font-medium text-white mb-3" data-id="element-7913">
                Plan Features
              </h3>
              <ul className="space-y-2" data-id="element-7914">
                {currentPlan.features.map((feature, index) => <li key={index} className="flex items-start" data-id="element-7915">
                    <CheckIcon className="h-5 w-5 text-green-400 mr-2 flex-shrink-0" data-id="element-7916" />
                    <span className="text-gray-300 text-sm" data-id="element-7917">{feature}</span>
                  </li>)}
              </ul>
            </div>
            <div data-id="element-7918">
              <h3 className="text-sm font-medium text-white mb-3" data-id="element-7919">
                Billing Information
              </h3>
              <div className="space-y-4" data-id="element-7920">
                <div className="flex items-start" data-id="element-7921">
                  <CalendarIcon className="h-5 w-5 text-gray-400 mr-2 flex-shrink-0" data-id="element-7922" />
                  <div data-id="element-7923">
                    <p className="text-white text-sm" data-id="element-7924">Next billing date</p>
                    <p className="text-gray-400 text-sm" data-id="element-7925">
                      {formatDate(billingInfo.nextBillingDate)}
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-7926">
                  <CreditCardIcon className="h-5 w-5 text-gray-400 mr-2 flex-shrink-0" data-id="element-7927" />
                  <div data-id="element-7928">
                    <p className="text-white text-sm" data-id="element-7929">Payment method</p>
                    <p className="text-gray-400 text-sm" data-id="element-7930">
                      {paymentMethods.find(method => method.isDefault)?.type === 'card' ? `${paymentMethods.find(method => method.isDefault)?.brand} •••• ${paymentMethods.find(method => method.isDefault)?.last4}` : paymentMethods.find(method => method.isDefault)?.name}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between" data-id="element-7931">
                  <div className="flex items-center" data-id="element-7932">
                    <span className="text-white text-sm mr-2" data-id="element-7933">Auto-renew</span>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${billingInfo.autoRenew ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'}`} data-id="element-7934">
                      {billingInfo.autoRenew ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <button onClick={toggleAutoRenew} disabled={isUpdatingBilling} className="text-deep-pink hover:text-burgundy text-sm" data-id="element-7935">
                    {isUpdatingBilling ? 'Updating...' : billingInfo.autoRenew ? 'Turn off' : 'Turn on'}
                  </button>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-navy-600" data-id="element-7936">
                <a href="/payment-methods" className="text-deep-pink hover:text-burgundy text-sm" data-id="element-7937">
                  Manage payment methods
                </a>
              </div>
            </div>
          </div>
        </div>
        {/* Available Plans */}
        <div className="bg-navy-800 rounded-xl p-6 mb-8" data-id="element-7938">
          <h2 className="text-lg font-medium text-white mb-4" data-id="element-7939">
            Available Plans
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-7940">
            {availablePlans.map(plan => <div key={plan.id} className={`border rounded-lg p-5 ${plan.isCurrent ? 'border-deep-pink bg-deep-pink/10' : selectedPlan === plan.id ? 'border-deep-pink bg-navy-700' : 'border-navy-600 bg-navy-700 hover:border-gray-500'} transition-colors relative`} data-id="element-7941">
                {plan.isPopular && !plan.isCurrent && <div className="absolute top-0 right-0 bg-deep-pink text-white text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-lg" data-id="element-7942">
                    Popular
                  </div>}
                <h3 className="text-lg font-medium text-white mb-2" data-id="element-7943">
                  {plan.name}
                </h3>
                <div className="mb-4" data-id="element-7944">
                  <span className="text-2xl font-bold text-white" data-id="element-7945">
                    ${plan.price.toFixed(2)}
                  </span>
                  <span className="text-gray-400 text-sm" data-id="element-7946">
                    /{plan.interval}
                  </span>
                </div>
                <ul className="space-y-2 mb-6" data-id="element-7947">
                  {plan.features.map((feature, index) => <li key={index} className="flex items-start" data-id="element-7948">
                      <CheckIcon className="h-5 w-5 text-green-400 mr-2 flex-shrink-0" data-id="element-7949" />
                      <span className="text-gray-300 text-sm" data-id="element-7950">{feature}</span>
                    </li>)}
                </ul>
                {plan.isCurrent ? <button disabled className="w-full py-2 bg-deep-pink text-white rounded-lg opacity-50 cursor-not-allowed" data-id="element-7951">
                    Current Plan
                  </button> : <button onClick={() => handlePlanSelect(plan.id)} className={`w-full py-2 rounded-lg ${selectedPlan === plan.id ? 'bg-deep-pink text-white' : 'bg-navy-600 text-white hover:bg-navy-500'} transition-colors`} data-id="element-7952">
                    {selectedPlan === plan.id ? 'Selected' : 'Select Plan'}
                  </button>}
              </div>)}
          </div>
          {selectedPlan && <div className="mt-6 flex justify-end" data-id="element-7953">
              <button onClick={handleChangePlan} disabled={isChangingPlan} className="px-4 py-2 bg-deep-pink hover:bg-burgundy disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors" data-id="element-7954">
                {isChangingPlan ? <span className="flex items-center" data-id="element-7955">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-7956">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-7957"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-7958"></path>
                    </svg>
                    Processing...
                  </span> : 'Switch Plan'}
              </button>
            </div>}
        </div>
        {/* Cancellation Dialog */}
        {showCancelDialog && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-7959">
            <div className="bg-navy-800 rounded-xl p-6 max-w-md w-full mx-4" data-id="element-7960">
              <h3 className="text-xl font-medium text-white mb-4" data-id="element-7961">
                Cancel Subscription
              </h3>
              <div className="mb-6" data-id="element-7962">
                <p className="text-gray-300 mb-4" data-id="element-7963">
                  We're sorry to see you go. Please let us know why you're
                  cancelling so we can improve our service.
                </p>
                <div className="mb-4" data-id="element-7964">
                  <label htmlFor="cancel-reason" className="block text-sm font-medium text-gray-300 mb-1" data-id="element-7965">
                    Reason for cancellation
                  </label>
                  <select id="cancel-reason" value={cancelReason} onChange={e => setCancelReason(e.target.value)} className="w-full px-3 py-2 bg-navy-700 border border-navy-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-deep-pink" data-id="element-7966">
                    <option value="" data-id="element-7967">Select a reason</option>
                    <option value="too_expensive" data-id="element-7968">Too expensive</option>
                    <option value="not_using" data-id="element-7969">Not using enough</option>
                    <option value="found_alternative" data-id="element-7970">
                      Found an alternative
                    </option>
                    <option value="missing_features" data-id="element-7971">Missing features</option>
                    <option value="poor_experience" data-id="element-7972">Poor experience</option>
                    <option value="other" data-id="element-7973">Other</option>
                  </select>
                </div>
                <div className="bg-amber-900/20 border border-amber-800 rounded-lg p-4 mb-4" data-id="element-7974">
                  <div className="flex" data-id="element-7975">
                    <AlertCircleIcon className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0" data-id="element-7976" />
                    <div data-id="element-7977">
                      <p className="text-amber-400 font-medium text-sm" data-id="element-7978">
                        What happens when you cancel
                      </p>
                      <ul className="text-gray-300 text-sm mt-2 space-y-1 list-disc pl-5" data-id="element-7979">
                        <li data-id="element-7980">
                          Your subscription will remain active until{' '}
                          {formatDate(billingInfo.nextBillingDate)}
                        </li>
                        <li data-id="element-7981">You will not be charged again after this period</li>
                        <li data-id="element-7982">
                          You'll lose access to premium features after your
                          subscription ends
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-3" data-id="element-7983">
                <button onClick={() => setShowCancelDialog(false)} className="px-4 py-2 border border-gray-500 text-gray-300 hover:bg-navy-700 rounded-lg transition-colors" data-id="element-7984">
                  Keep Subscription
                </button>
                <button onClick={handleCancelSubscription} disabled={!cancelReason || isUpdatingBilling} className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors" data-id="element-7985">
                  {isUpdatingBilling ? 'Processing...' : 'Confirm Cancellation'}
                </button>
              </div>
            </div>
          </div>}
      </div>
    </StudentLayout>;
};